import React, { useState, useEffect, useCallback } from 'react';
import { Star, Edit, Trash2 } from 'lucide-react';
import { reviewsApi } from '../services/api';
import { Review } from '../types';
import ReviewForm from './ReviewForm';
import { confirmAsync, showInfo } from '../utils/toast';
import { extractApiError } from '../utils/errorUtils'; // Issue #964: Standardized error handling

interface ReviewListProps {
  fairId: number;
  currentUserId?: number;
}

const REVIEWS_PAGE_SIZE = 20;

export default function ReviewList({ fairId, currentUserId }: ReviewListProps) {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingReview, setEditingReview] = useState<Review | null>(null);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalReviews, setTotalReviews] = useState(0);

  const loadReviews = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      const data = await reviewsApi.getFairReviews(fairId, page, REVIEWS_PAGE_SIZE);
      // Defensive programming: ensure items is always an array
      setReviews(data?.items || []);
      setTotalPages(data?.total_pages || 1);
      setTotalReviews(data?.total || 0);
      setCurrentPage(page);
    } catch (err: unknown) {
      console.error('Failed to load reviews:', err);
      const message = extractApiError(err).message;
      setError(message);
      // Reset to empty array on error
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, [fairId]);

  useEffect(() => {
    // Reset to page 1 when fair changes to avoid empty results
    setCurrentPage(1);
    loadReviews(1);
  }, [fairId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeleteReview = async () => {
    const confirmed = await confirmAsync('Are you sure you want to delete this review?');
    if (!confirmed) return;

    try {
      await reviewsApi.deleteReview(fairId);
      await loadReviews();
    } catch (err: unknown) {
      console.error('Failed to delete review:', err);
      const message = extractApiError(err).message;
      showInfo(message);
    }
  };

  const handleReviewSuccess = async () => {
    setEditingReview(null);
    setShowReviewForm(false);
    await loadReviews();
  };

  // Defensive programming: ensure reviews is always an array
  const userReview = reviews?.find(r => r.user_id === currentUserId);
  const otherReviews = reviews?.filter(r => r.user_id !== currentUserId) || [];

  const averageRating = reviews && reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : '0.0';

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="text-gray-600">Loading reviews...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Review Summary */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Customer Reviews</h3>

        <div className="flex items-center space-x-4 mb-6">
          <div className="flex items-center">
            <Star className="h-6 w-6 fill-yellow-400 text-yellow-400" />
            <span className="ml-2 text-3xl font-bold text-gray-900">{averageRating}</span>
          </div>
          <div className="text-gray-600">
            <div className="font-medium">{totalReviews} {totalReviews === 1 ? 'review' : 'reviews'}</div>
          </div>
        </div>

        {/* Write Review Button */}
        {currentUserId && !userReview && !showReviewForm && (
          <button
            onClick={() => setShowReviewForm(true)}
            className="w-full px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors font-medium"
          >
            Write a Review
          </button>
        )}

        {/* Review Form */}
        {showReviewForm && !editingReview && (
          <div className="mt-6">
            <ReviewForm
              fairId={fairId}
              onSuccess={handleReviewSuccess}
              onCancel={() => setShowReviewForm(false)}
            />
          </div>
        )}
      </div>

      {/* User's Review (if exists) */}
      {userReview && !editingReview && (
        <div className="bg-teal-50 border-2 border-teal-200 rounded-lg p-6">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="flex items-center space-x-2 mb-1">
                <div className="flex text-yellow-400">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`h-4 w-4 ${i < userReview.rating ? 'fill-current' : ''}`}
                    />
                  ))}
                </div>
                <span className="text-sm font-medium text-primary-dark">Your Review</span>
              </div>
              <h4 className="font-semibold text-gray-900">{userReview.title}</h4>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setEditingReview(userReview)}
                className="p-2 text-secondary hover:bg-moonbone rounded-full transition-colors"
                title="Edit review"
              >
                <Edit className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleDeleteReview()}
                className="p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                title="Delete review"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
          <p className="text-gray-700">{userReview.comment}</p>
          <p className="mt-2 text-sm text-gray-500">
            {userReview.created_at ? new Date(userReview.created_at).toLocaleDateString() : ''}
          </p>
        </div>
      )}

      {/* Edit Form */}
      {editingReview && (
        <div className="bg-white rounded-lg shadow p-6">
          <ReviewForm
            fairId={fairId}
            existingReview={{
              rating: editingReview.rating,
              title: editingReview.title,
              comment: editingReview.comment,
            }}
            onSuccess={handleReviewSuccess}
            onCancel={() => setEditingReview(null)}
          />
        </div>
      )}

      {/* Other Reviews */}
      {otherReviews.length > 0 && (
        <div className="space-y-4">
          <h4 className="font-semibold text-gray-900">All Reviews ({otherReviews.length})</h4>
          {otherReviews.map((review) => (
            <div key={review.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center space-x-2 mb-1">
                    <div className="flex text-yellow-400">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`h-4 w-4 ${i < review.rating ? 'fill-current' : ''}`}
                        />
                      ))}
                    </div>
                    <span className="text-sm text-gray-600">
                      {review.user_display_name || 'Anonymous'}
                    </span>
                  </div>
                  <h4 className="font-semibold text-gray-900">{review.title}</h4>
                </div>
                <span className="text-sm text-gray-500">
                  {review.created_at ? new Date(review.created_at).toLocaleDateString() : ''}
                </span>
              </div>
              <p className="text-gray-700">{review.comment}</p>
            </div>
          ))}
        </div>
      )}

      {/* No Reviews Message */}
      {reviews.length === 0 && !loading && (
        <div className="text-center py-8 text-gray-500">
          <p>No reviews yet. Be the first to review this artist fair!</p>
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && !loading && (
        <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-6">
          <div className="text-sm text-gray-700">
            Showing <span className="font-medium">{Math.min((currentPage - 1) * REVIEWS_PAGE_SIZE + 1, totalReviews)}</span> to{' '}
            <span className="font-medium">{Math.min(currentPage * REVIEWS_PAGE_SIZE, totalReviews)}</span> of{' '}
            <span className="font-medium">{totalReviews}</span> reviews
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => loadReviews(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-3 py-2 text-sm font-medium rounded-md ${
                currentPage === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
              }`}
            >
              Previous
            </button>
            <div className="flex items-center space-x-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(page => {
                  // Show first page, last page, current page, and pages around current
                  return (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  );
                })
                .map((page, index, array) => (
                  <React.Fragment key={page}>
                    {index > 0 && array[index - 1] !== page - 1 && (
                      <span className="px-2 text-gray-400">...</span>
                    )}
                    <button
                      onClick={() => loadReviews(page)}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        currentPage === page
                          ? 'bg-primary text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                      }`}
                    >
                      {page}
                    </button>
                  </React.Fragment>
                ))}
            </div>
            <button
              onClick={() => loadReviews(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-3 py-2 text-sm font-medium rounded-md ${
                currentPage === totalPages
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
